/**
 * train-api.ts — API của màn cấu hình nhân viên AI (nháp/phát hành, công cụ persona, mẫu).
 * Tách riêng khỏi hooks/use-ai.ts để không đụng file dùng chung.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type { AiBot, AiBotInput } from '@/hooks/use-ai'
import type { BotSettings, CollectAttribute, ConditionOp } from './bot-settings'
import { i18n } from '@/lib/i18n'

export type TrainBot = AiBot & { settings?: Partial<BotSettings>; publishedVersionId?: string | null }
export type TrainBotInput = AiBotInput & { settings?: BotSettings }

export const trainKeys = {
  bot: (id: string) => ['ai', 'train', 'bot', id] as unknown[],
  versions: (id: string) => ['ai', 'train', 'versions', id] as unknown[],
  templates: ['ai', 'persona-templates'] as unknown[],
}

/** Lượt lưu nháp đang chạy theo bot — lần tải bot kế tiếp chờ nó xong để không đọc phải bản cũ (rời màn rồi vào lại ngay). */
const pendingSaves = new Map<string, Promise<unknown>>()

export function useTrainBot(botId: string | undefined) {
  return useQuery<TrainBot>({
    queryKey: trainKeys.bot(botId ?? ''),
    enabled: !!botId,
    queryFn: async () => {
      await pendingSaves.get(botId!)?.catch(() => {})
      return (await api.get<{ bot: TrainBot }>(`/ai/bots/${botId}`)).data.bot
    },
    staleTime: Infinity, // màn tự lưu — không tự tải lại đè lên nội dung đang gõ
    refetchOnWindowFocus: false,
    // Mỗi lần MỞ màn phải lấy bản mới nhất từ máy chủ: bản đệm không được cập nhật sau mỗi lượt tự lưu, giữ lại thì
    // vào lại màn sẽ thấy cấu hình cũ (kênh áp dụng, persona… như chưa lưu) và sửa tiếp sẽ ghi đè bản đã lưu.
    gcTime: 0,
    refetchOnMount: 'always',
  })
}

export async function saveTrainBot(id: string, input: TrainBotInput) {
  const req = api.put<{ bot: TrainBot; warnings?: string[] }>(`/ai/bots/${id}`, input)
  pendingSaves.set(id, req)
  try {
    return (await req).data
  } finally {
    if (pendingSaves.get(id) === req) pendingSaves.delete(id)
  }
}

export type BotVersionRow = {
  id: string
  version: number
  note: string | null
  publishedAt: string
  publishedBy: string | null
  publishedByName: string | null
  isCurrent: boolean
}
export type BotVersionsInfo = {
  publishedVersionId: string | null
  publishedVersion: number | null
  neverPublished: boolean
  hasUnpublishedChanges: boolean
  draftUpdatedAt: string
  versions: BotVersionRow[]
}

export function useBotVersions(botId: string | undefined) {
  return useQuery<BotVersionsInfo>({
    queryKey: trainKeys.versions(botId ?? ''),
    enabled: !!botId,
    queryFn: async () => (await api.get<BotVersionsInfo>(`/ai/bots/${botId}/versions`)).data,
  })
}

export type BotSnapshot = {
  personaPrompt: string | null
  playbookPrompt: string | null
  toolsJson: unknown
  provider: string | null
  model: string | null
  settings: BotSettings
}
export async function fetchBotVersion(botId: string, versionId: string) {
  const { data } = await api.get<{ version: { id: string; version: number; note: string | null; publishedAt: string; snapshot: BotSnapshot } }>(
    `/ai/bots/${botId}/versions/${versionId}`,
  )
  return data.version
}

export async function publishTrainBot(botId: string, note: string) {
  const { data } = await api.post<{ version: { id: string; version: number } }>(`/ai/bots/${botId}/publish`, { note })
  return data.version
}

export async function restoreTrainBot(botId: string, versionId: string) {
  const { data } = await api.post<{ restored: number }>(`/ai/bots/${botId}/restore`, { versionId })
  return data
}

/** Chat thử trên một phiên bản đã phát hành (dùng ở "Lịch sử phiên bản"). */
export async function simulateVersionReply(conversationId: string, customerText: string, botId: string, versionId: string) {
  const { data } = await api.post<{ reply: string | null; bubbles?: string[]; handoff?: { should: boolean; reason?: string } | null; handoffNotice?: string | null }>(
    '/ai/simulate/reply', { conversationId, customerText, botId, versionId, includeTrace: false },
  )
  return data
}

// ── Công cụ persona ──────────────────────────────────────────────────

export type PersonaTone = 'friendly' | 'natural' | 'gentle' | 'professional' | 'humorous'
export type PersonaLength = 'short' | 'standard' | 'detailed'
export const TONE_OPTIONS: Array<{ value: PersonaTone; label: string }> = [
  { value: 'friendly', get label() { return i18n.t('aiTrainLib:botSettings.tone.friendly') } },
  { value: 'natural', get label() { return i18n.t('aiTrainLib:botSettings.tone.natural') } },
  { value: 'gentle', get label() { return i18n.t('aiTrainLib:botSettings.tone.gentle') } },
  { value: 'professional', get label() { return i18n.t('aiTrainLib:botSettings.tone.professional') } },
  { value: 'humorous', get label() { return i18n.t('aiTrainLib:botSettings.tone.humorous') } },
]
export const LENGTH_OPTIONS: Array<{ value: PersonaLength; label: string }> = [
  { value: 'short', get label() { return i18n.t('aiTrainLib:botSettings.length.short') } },
  { value: 'standard', get label() { return i18n.t('aiTrainLib:botSettings.length.standard') } },
  { value: 'detailed', get label() { return i18n.t('aiTrainLib:botSettings.length.detailed') } },
]

export async function generatePersona(input: { botId: string; description: string; tone: PersonaTone; length: PersonaLength }) {
  return (await api.post<{ content: string }>('/ai/persona/generate', input)).data.content
}
export async function optimizePersona(input: { botId: string; current: string; request: string }) {
  return (await api.post<{ content: string }>('/ai/persona/optimize', input)).data.content
}

export type PersonaIssue = {
  type: 'conflict' | 'vague' | 'infeasible' | 'missing' | 'too_long' | 'placeholder'
  severity: 'high' | 'medium' | 'low'
  excerpt: string
  message: string
  suggestion: string
}
export async function checkPersona(current: string) {
  return (await api.post<{ summary: string; score: number | null; issues: PersonaIssue[] }>('/ai/persona/check', { current })).data
}

export type PersonaTemplate = { id: string; group: string; name: string; industry: string; summary: string; content: string }
export function usePersonaTemplates(enabled: boolean) {
  return useQuery<{ groups: Array<{ id: string; label: string }>; templates: PersonaTemplate[] }>({
    queryKey: trainKeys.templates,
    enabled,
    staleTime: Infinity,
    queryFn: async () => (await api.get('/ai/persona-templates')).data,
  })
}

// ── Danh mục biến & công cụ (GET /ai/bots/:id/variable-catalog) ─────────────

export type CatalogVariable = {
  source: 'customer' | 'agent' | 'system'
  id: string
  code: string
  name: string
  type: 'string' | 'enum' | 'multi' | 'number'
  variable: string
  operators: ConditionOp[]
  options: Array<{ value: string; label: string }>
  /** Thuộc tính tuỳ chỉnh của tổ chức (nhóm "Thuộc tính tuỳ chỉnh"). */
  custom?: boolean
  /** Giải thích ngắn (ⓘ). */
  description?: string
  conditionable?: boolean
}
export type CatalogTool = { id: string; name: string; token: string; description: string; group: string }
export type VariableCatalog = {
  groups: Array<{ id: 'customer' | 'agent' | 'system'; label: string; variables: CatalogVariable[] }>
  tools: CatalogTool[]
}

export function useVariableCatalog(botId: string | undefined) {
  return useQuery<VariableCatalog>({
    queryKey: ['ai', 'train', 'variable-catalog', botId ?? ''],
    enabled: !!botId,
    staleTime: 60_000,
    queryFn: async () => (await api.get<VariableCatalog>(`/ai/bots/${botId}/variable-catalog`)).data,
  })
}

/**
 * Nhóm "thuộc tính AI thu thập" lấy từ form đang soạn (chưa chắc đã tự lưu) thay cho bản server.
 * Hàm thuần — test ở train-lib.test.ts.
 */
export function withLiveAttributes(cat: VariableCatalog | undefined, attrs: CollectAttribute[]): VariableCatalog | undefined {
  if (!cat) return cat
  const agent: CatalogVariable[] = attrs.map((a) => {
    const type = a.type === 'number' ? 'number' : a.type === 'select' ? 'enum' : 'string'
    const operators: ConditionOp[] = type === 'number'
      ? ['=', '!=', '>', '<', '>=', '<=', 'is_null', 'is_not_null']
      : ['=', '!=', 'in', 'not_in', 'is_null', 'is_not_null']
    return {
      source: 'agent', id: `agent.${a.key}`, code: a.key, name: a.name, type, variable: `{{var.agent.${a.key}}}`,
      operators, options: (a.options ?? []).map((o) => ({ value: o, label: o })),
    }
  })
  return { ...cat, groups: cat.groups.map((g) => (g.id === 'agent' ? { ...g, variables: agent } : g)) }
}

// ── Danh mục công cụ theo nền tảng (GET /ai/tool-catalog) ─────────────────────

export type PlatformToolKind = 'products' | 'stock' | 'orders' | 'member' | 'order_create'
export interface PlatformToolDef { id: string; kind: PlatformToolKind; name: string; description: string; hasData: boolean }
export interface PlatformEntry {
  id: string
  name: string
  comingSoon: boolean
  connected: boolean
  enabled: boolean
  aiAuthorized: boolean
  /** Uỷ quyền RIÊNG cho AI tạo đơn nháp — mở rộng thêm trên aiAuthorized, không thay thế. */
  aiOrderAuthorized: boolean
  settingsPath: string
  tools: PlatformToolDef[]
}
export const toolCatalogKey = ['ai', 'tool-catalog'] as unknown[]
export function useToolCatalog(enabled: boolean) {
  return useQuery<{ platforms: PlatformEntry[] }>({
    queryKey: toolCatalogKey,
    enabled,
    queryFn: async () => (await api.get('/ai/tool-catalog')).data,
  })
}
